import type { TDocumentDefinitions } from 'pdfmake/interfaces.js';
import { formatDay } from '../../format.js';
import { zonedStamp } from '../../inputs/lt-utc.js';
import { tablePdf } from '../fuel/reconciliation-form.js';
import { sheetsWorkbook } from '../reports/xlsx.js';
import type { DrillCompetency } from './finding-types.js';
import { safeFileBase } from './report-pdf.js';

type T = (key: string, options?: Record<string, unknown>) => string;

/**
 * Mürettebat yetkinlik matrisinin dışa aktarımı (ortağın belgesi §6; ADR 0128 Faz 5 Ek): ekrandaki
 * matrisin aynısı, kişi × tatbikat; hücre son kaptan onaylı katılımın tarihi, görevi ve
 * değerlendirmesi; "!" eksik zorunlu tatbikat (SOLAS III/19.3.2). PDF yatay A4, Excel tek sayfa.
 */
export function competencyTable(data: DrillCompetency, t: T) {
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`drillReport.competency.${key}`, options);
  const rating = (value: string | null) =>
    value === 'satisfactory'
      ? t('drillRecord.evaluation.satisfactory')
      : value === 'needs_improvement'
        ? t('drillRecord.evaluation.needsImprovement')
        : null;
  const { matrix } = data;
  const mandatory = new Set(
    matrix.drills
      .filter((d) => matrix.rows.some((r) => r.cells[d.templateId]?.mandatory))
      .map((d) => d.templateId),
  );
  const head = [
    k('person'),
    k('rank'),
    ...matrix.drills.map((d) => `${d.code}${mandatory.has(d.templateId) ? ' *' : ''}`),
    k('last'),
    k('missing'),
    k('trainingNeeds'),
  ];
  const rows = matrix.rows.map((row) => [
    row.name,
    row.rank ?? '—',
    ...matrix.drills.map((d) => {
      const cell = row.cells[d.templateId];
      if (!cell) return '';
      const text = cell.lastOn
        ? [formatDay(cell.lastOn), cell.role, rating(cell.rating)].filter(Boolean).join(' · ')
        : k('never');
      return cell.missing ? `! ${text}` : text;
    }),
    row.lastOn ? formatDay(row.lastOn) : '—',
    row.missing.join(', ') || '—',
    String(row.openTrainingNeeds),
  ]);
  return { head, rows, drillCount: matrix.drills.length };
}

function vesselName(data: DrillCompetency): string {
  return data.vessels.find((v) => v.id === data.vesselId)?.name ?? data.vessels[0]?.name ?? '—';
}

export function competencyPdf(
  data: DrillCompetency,
  t: T,
  printedAt: string,
): TDocumentDefinitions {
  const k = (key: string) => t(`drillReport.competency.${key}`);
  const { head, rows, drillCount } = competencyTable(data, t);
  const today = data.vessels.find((v) => v.id === data.vesselId)?.today ?? null;
  const definition = tablePdf({
    title: k('title'),
    info: [
      [t('drillReport.fields.vessel'), vesselName(data)],
      [t('drillReport.fields.date'), today ? formatDay(today) : '—'],
      [
        t('drillFinding.competency.people'),
        `${data.matrix.summary.people} · ${t('drillFinding.competency.peopleMissing')}: ${data.matrix.summary.missing}`,
      ],
    ],
    tables: [
      {
        head,
        rows,
        numeric: [head.length - 1],
      },
    ],
    notes: [`* ${k('mandatory')}`, k('legend')],
    footer: `${k('title')} · ${t('recordPdf.printed', { at: `${zonedStamp(printedAt)} UTC` })} · Felixmate`,
  });
  // Matris geniş: sütunlar eşit paylaşılır, metin hücrede kırılır; çok tatbikatta yazı küçülür
  const [, , matrixNode] = definition.content as { table?: { widths?: unknown[] } }[];
  if (matrixNode?.table) matrixNode.table.widths = head.map((_, i) => (i === 0 ? 70 : '*'));
  if (drillCount > 8) definition.defaultStyle = { ...definition.defaultStyle, fontSize: 6.5 };
  return definition;
}

export async function competencyWorkbook(data: DrillCompetency, t: T): Promise<Uint8Array> {
  const { head, rows } = competencyTable(data, t);
  return sheetsWorkbook([
    {
      name: t('drillReport.competency.sheet'),
      head,
      rows: rows.map((r) => r.map((cell, index) => (index === r.length - 1 ? Number(cell) : cell))),
    },
  ]);
}

/** Dosya adı: "crew-competency-MV-Aurora-2026-10-26.pdf" (gemide de kaydedilebilir). */
export function competencyFileName(data: DrillCompetency, extension: 'pdf' | 'xlsx') {
  const today = data.vessels.find((v) => v.id === data.vesselId)?.today ?? '';
  return `${safeFileBase(`crew-competency-${vesselName(data)}-${today}`)}.${extension}`;
}
